import { useState, useEffect, useCallback, useRef } from 'react';
import { getCommandCenter } from '../../lib/api';
import { TONE, StatusChip } from './ui';
import { X, ArrowsIn, Fire, Warning, Handshake, Buildings, CheckCircle, ChatCircleText, ClockCountdown } from '@phosphor-icons/react';

/*
 Conference Room Mode = wall-TV huddle display.
 - Fills the entire screen with big text and high contrast.
 - Auto-scrolls the Command Queue every 8 seconds.
 - Refreshes KPIs every 20 seconds.
 - Escape key or big Exit button to close.
*/

export default function ConferenceRoomMode({ open, onClose }) {
  const [cc, setCc] = useState(null);
  const [scrollIdx, setScrollIdx] = useState(0);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    try { setCc(await getCommandCenter()); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    const dataIv = setInterval(load, 20000);
    return () => clearInterval(dataIv);
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const scrollIv = setInterval(() => setScrollIdx(i => i + 1), 8000);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearInterval(scrollIv); window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  if (!open) return null;

  const kpis = cc?.kpis || {};
  const queue = cc?.queue || [];
  const visibleStart = queue.length > 0 ? scrollIdx % queue.length : 0;
  // Show 3 rotating items at a time
  const visible = queue.length === 0 ? [] : [0, 1, 2].map(o => queue[(visibleStart + o) % queue.length]);

  const now = new Date();
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div ref={containerRef} className="fixed inset-0 z-[300] bg-[#0A0A0A] text-foreground overflow-hidden flex flex-col" data-testid="conference-room-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-border">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Team Hamilton Daily Burn</p>
          <h1 className="text-4xl font-bold text-foreground mt-1">Command Center · Live</h1>
          <p className="text-lg text-muted-foreground mt-1">{dateStr}</p>
        </div>
        <div className="text-right flex items-center gap-6">
          <div>
            <p className="text-6xl font-bold text-foreground tabular-nums">{timeStr}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground text-right">Central Time</p>
          </div>
          <button onClick={onClose}
            className="w-14 h-14 rounded-lg bg-card border border-border hover:bg-muted flex items-center justify-center transition-colors"
            data-testid="conference-close-btn" title="Exit (Esc)">
            <ArrowsIn size={22} />
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-3 gap-4 px-8 py-6 border-b border-border">
        <BigKpi icon={Warning} tone={kpis.needs_action_critical > 0 ? 'critical' : 'high'}
          label="Needs Action Today" value={kpis.needs_action_today || 0}
          sub={kpis.needs_action_critical > 0 ? `${kpis.needs_action_critical} critical` : 'On track'} />
        <BigKpi icon={ClockCountdown} tone={kpis.commitments_overdue > 0 ? 'critical' : 'high'}
          label="Commitments at Risk" value={kpis.commitments_at_risk || 0}
          sub={kpis.commitments_overdue > 0 ? `${kpis.commitments_overdue} overdue` : 'Within 7 days'} />
        <BigKpi icon={ChatCircleText} tone={kpis.client_experience_alerts > 0 ? 'high' : 'good'}
          label="Client Experience" value={kpis.client_experience_alerts || 0}
          sub={kpis.client_experience_new_today > 0 ? `${kpis.client_experience_new_today} new today` : 'No new flags'} />
        <BigKpi icon={Handshake} tone="info"
          label="Waiting on 3rd Party" value={kpis.waiting_third_party || 0}
          sub="Borrower / Realtor / Title" />
        <BigKpi icon={CheckCircle} tone="good"
          label="Clear for Today" value={kpis.clear_for_today || 0} sub="On track" />
        <BigKpi icon={Buildings} tone="info"
          label="Closings This Week" value={kpis.closings_this_week || 0}
          sub={kpis.closings_this_week_volume ? `$${(kpis.closings_this_week_volume / 1_000_000).toFixed(1)}M volume` : 'Next 7 days'} />
      </div>

      {/* Rotating Queue */}
      <div className="flex-1 px-8 py-6 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Fire size={26} weight="fill" className="text-[#E63935]" />
            <h2 className="text-3xl font-bold">Today&apos;s Command Queue</h2>
            <span className="text-sm text-muted-foreground">Auto-scrolling · showing {Math.min(3, queue.length)} of {queue.length}</span>
          </div>
          <span className="inline-flex items-center gap-2 text-sm text-emerald-500 font-semibold">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> LIVE
          </span>
        </div>
        {queue.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <CheckCircle size={64} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
              <p className="text-2xl text-foreground">All clear. Nothing needs immediate attention.</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 gap-3">
            {visible.map((item, i) => {
              const t = TONE[item.priority] || TONE.neutral;
              return (
                <div key={`${item.source_type}-${item.source_id || i}-${visibleStart}`}
                  className={`rounded-lg border ${t.border} ${t.bg} p-5 flex items-center gap-5 transition-opacity`}>
                  <span className={`w-2 h-16 rounded-full ${t.dot} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <p className="text-3xl font-bold text-foreground">{item.borrower}</p>
                      <StatusChip tone={item.priority}>{item.priority.toUpperCase()}</StatusChip>
                      <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{item.category.replace('_', ' ')}</span>
                    </div>
                    <p className="text-xl text-foreground/90 mt-2">{item.issue}</p>
                    {item.owner && <p className="text-sm text-muted-foreground mt-1">Owner: <span className="text-foreground font-semibold">{item.owner}</span></p>}
                  </div>
                  <div className="text-right">
                    <p className={`text-3xl font-bold ${t.text}`}>{item.due_label}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Due / Impact</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-3 border-t border-border flex items-center justify-between text-sm text-muted-foreground">
        <span>Press <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border text-foreground text-xs">Esc</kbd> to exit conference room mode</span>
        <span>Data refresh · every 20s · queue rotate · every 8s</span>
      </div>
    </div>
  );
}

function BigKpi({ icon: Icon, tone = 'neutral', label, value, sub }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} p-5 flex items-center gap-4`}>
      <div className={`w-14 h-14 rounded-lg flex items-center justify-center border ${t.bg} ${t.border} flex-shrink-0`}>
        <Icon size={26} weight="fill" className={t.text} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="text-5xl font-bold text-foreground tabular-nums leading-tight mt-0.5">{value}</p>
        <p className={`text-sm mt-1 ${t.text}`}>{sub}</p>
      </div>
    </div>
  );
}
